import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Minus, Plus, Ban, DoorOpen, PauseCircle, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getEventoVisibleByIdParaSocio,
  hidratarEventosPublicos,
} from "@/data/adminEventosStore";
import { getCurrentSocio } from "@/data/socioAuthStore";
import { obtenerCompraContexto, crearCompraAG } from "@/data/comprasStore";
import {
  restaurarCantidadesAG,
  guardarCantidadesAG,
  idempotencyKeyCarritoAG,
  limpiarCarritoAG,
} from "@/data/carritoAccesoStore";
import { ACCESO_GENERAL as T } from "@/constants/testIds";
import { toast } from "sonner";

const fmt = (n) => `$${Number(n || 0).toLocaleString("es-MX")} MXN`;

const MSG_ERROR = {
  SIN_DISPONIBILIDAD: "Algunos lugares que elegiste ya se ocuparon. Ajustamos las cantidades disponibles.",
  SALON_INACTIVO: "Un salón dejó de estar disponible. Revisa tu selección.",
  SALON_INVALIDO: "Un salón ya no existe para este evento. Revisa tu selección.",
  LIMITE_ALCANZADO: "Alcanzaste el límite de boletos permitido para este evento.",
  VENTA_PAUSADA: "La venta de este evento está pausada por el momento.",
  CONFLICTO_IDEMPOTENCIA: "Detectamos un cambio en tu carrito. Vuelve a intentarlo.",
  network: "No pudimos conectarnos al servidor. Verifica tu conexión e intenta de nuevo.",
};

export default function SeleccionAccesoGeneral() {
  const { id } = useParams();
  const navigate = useNavigate();
  const socio = getCurrentSocio();

  const [hidratado, setHidratado] = useState(false);
  const [contexto, setContexto] = useState(null);
  const [cantidades, setCantidades] = useState({});
  const [paso, setPaso] = useState("seleccion"); // seleccion | resumen
  const [aviso, setAviso] = useState("");
  const [salonAfectado, setSalonAfectado] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const enviandoRef = useRef(false);

  useEffect(() => {
    hidratarEventosPublicos().finally(() => setHidratado(true));
  }, []);

  // Cada cambio de pantalla inicia arriba.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [paso]);

  const refrescarContexto = useCallback(async () => {
    if (!id) return null;
    const ctx = await obtenerCompraContexto(id);
    if (ctx && !ctx.error) setContexto(ctx);
    return ctx;
  }, [id]);

  useEffect(() => {
    refrescarContexto();
    const onFocus = () => refrescarContexto();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refrescarContexto]);

  // Restaura cantidades del carrito (sólo ids+cantidades) al montar.
  useEffect(() => {
    if (!id || !socio?.id) return;
    const guardadas = restaurarCantidadesAG(id, socio.id);
    if (guardadas && Object.keys(guardadas).length > 0) setCantidades(guardadas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, socio?.id]);

  const evento = getEventoVisibleByIdParaSocio(id);

  if (!evento) {
    if (!hidratado) return null;
    return <Navigate to="/app" replace />;
  }
  // Ruta incompatible con la modalidad → redirige a la pantalla correcta.
  if (evento.modalidad !== "ACCESO_GENERAL") {
    return <Navigate to={`/app/eventos/${id}/seleccion-mesa`} replace />;
  }

  const salones = Array.isArray(contexto?.salones) ? contexto.salones : [];

  const cupoRestante = contexto?.cupoRestante; // null = sin límite
  const limite = contexto?.limiteBoletosPorUsuario || 0;
  const confirmados = contexto?.confirmados || 0;
  const totalSeleccionado = Object.values(cantidades).reduce(
    (s, n) => s + (Number(n) || 0),
    0,
  );
  const totalGeneral = salones.reduce(
    (s, sal) => s + (Number(cantidades[sal.id]) || 0) * Number(sal.precioPorBoleto || 0),
    0,
  );
  const cupoDespues =
    cupoRestante === null || cupoRestante === undefined
      ? null
      : Math.max(0, cupoRestante - totalSeleccionado);
  const remanenteCupo =
    cupoRestante === null || cupoRestante === undefined
      ? Infinity
      : Math.max(0, cupoRestante - totalSeleccionado);

  const bloqueadoPorLimite = contexto && cupoRestante !== null && cupoRestante <= 0;
  const ventaPausada = !!contexto?.ventaPausada;

  const setCant = (salonId, next) => {
    setCantidades((prev) => {
      const merged = { ...prev, [salonId]: next };
      if (next <= 0) delete merged[salonId];
      if (socio?.id) guardarCantidadesAG(id, socio.id, merged);
      return merged;
    });
  };

  const inc = (s) => {
    const actual = Number(cantidades[s.id]) || 0;
    if (actual >= s.disponibles || remanenteCupo <= 0) return;
    setCant(s.id, actual + 1);
    setAviso(`Boletos de ${s.salon}: ${actual + 1}`);
  };
  const dec = (s) => {
    const actual = Number(cantidades[s.id]) || 0;
    if (actual <= 0) return;
    setCant(s.id, actual - 1);
    setAviso(`Boletos de ${s.salon}: ${actual - 1}`);
  };

  // Reconciliar SOLO hacia abajo contra disponibilidad/cupo autoritativos.
  // Devuelve { cambiado, afectado } sin aumentar nunca cantidades.
  const reconciliar = (ctx) => {
    const sals = Array.isArray(ctx?.salones) ? ctx.salones : [];
    const dispMap = new Map(sals.map((s) => [s.id, s]));
    let cambiado = false;
    let afectado = null;
    const nuevo = {};
    let acumulado = 0;
    const cupo =
      ctx?.cupoRestante === null || ctx?.cupoRestante === undefined
        ? Infinity
        : ctx.cupoRestante;
    // Orden determinista por orden/id del backend.
    for (const s of sals) {
      const pedido = Number(cantidades[s.id]) || 0;
      if (pedido <= 0) continue;
      const topeSalon = Math.max(0, Number(s.disponibles) || 0);
      const topeCupo = Math.max(0, cupo - acumulado);
      const permitido = Math.min(pedido, topeSalon, topeCupo);
      if (permitido !== pedido) {
        cambiado = true;
        if (!afectado) afectado = s.id;
      }
      if (permitido > 0) {
        nuevo[s.id] = permitido;
        acumulado += permitido;
      }
    }
    // Salones que ya no existen en el contexto → se descartan.
    for (const sid of Object.keys(cantidades)) {
      if (!dispMap.has(sid) && Number(cantidades[sid]) > 0) {
        cambiado = true;
        if (!afectado) afectado = sid;
      }
    }
    return { cambiado, afectado, nuevo };
  };

  const irAResumen = async () => {
    if (totalSeleccionado <= 0) return;
    const ctx = await refrescarContexto();
    if (ctx && !ctx.error) {
      const { cambiado, afectado, nuevo } = reconciliar(ctx);
      if (cambiado) {
        setCantidades(nuevo);
        if (socio?.id) guardarCantidadesAG(id, socio.id, nuevo);
        setSalonAfectado(afectado);
        setAviso(MSG_ERROR.SIN_DISPONIBILIDAD);
        return;
      }
    }
    setSalonAfectado(null);
    setPaso("resumen");
  };

  const confirmarCompra = async () => {
    if (enviandoRef.current) return; // idempotencia frontend (doble clic)
    const ctx = await refrescarContexto();
    if (ctx && !ctx.error) {
      const { cambiado, afectado, nuevo } = reconciliar(ctx);
      if (cambiado) {
        setCantidades(nuevo);
        if (socio?.id) guardarCantidadesAG(id, socio.id, nuevo);
        setSalonAfectado(afectado);
        setAviso(MSG_ERROR.SIN_DISPONIBILIDAD);
        setPaso("seleccion");
        return;
      }
    }
    enviandoRef.current = true;
    setEnviando(true);
    const items = salones
      .map((s) => ({ salonAccesoId: s.id, cantidad: Number(cantidades[s.id]) || 0 }))
      .filter((it) => it.cantidad > 0);
    const idem = idempotencyKeyCarritoAG(id, socio.id, cantidades);
    const compra = await crearCompraAG({ eventoId: id, items, idempotencyKey: idem });
    enviandoRef.current = false;
    setEnviando(false);
    if (compra && !compra.error) {
      limpiarCarritoAG();
      navigate(`/app/eventos/${id}/pago?compra=${encodeURIComponent(compra.id)}`, {
        state: { modalidad: "ACCESO_GENERAL", compra },
      });
      return;
    }
    const reason = compra?.error;
    if (reason === "LIMITE_ALCANZADO" || reason === "VENTA_PAUSADA") {
      setAviso(MSG_ERROR[reason]);
      toast.error(MSG_ERROR[reason]);
      setPaso("seleccion");
      await refrescarContexto();
      return;
    }
    // Disponibilidad/salón: volver a selección, refrescar y resaltar.
    if (["SIN_DISPONIBILIDAD", "SALON_INACTIVO", "SALON_INVALIDO"].includes(reason)) {
      const ctx2 = await refrescarContexto();
      const { nuevo, afectado } = reconciliar(ctx2 || {});
      setCantidades(nuevo);
      if (socio?.id) guardarCantidadesAG(id, socio.id, nuevo);
      setSalonAfectado(afectado || compra?.meta?.salonAccesoId || null);
      setAviso(MSG_ERROR[reason] || "Revisa tu selección.");
      setPaso("seleccion");
      return;
    }
    setAviso(compra?.mensaje || MSG_ERROR.network);
    toast.error(compra?.mensaje || MSG_ERROR.network);
  };

  const volverAlEvento = () => navigate(`/app/eventos/${id}`);

  const BotonRegresar = ({ onClick, testId }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-label="Regresar"
      className="self-start inline-flex items-center gap-2 rounded-full pl-2 pr-4 py-2 text-sm font-medium border hairline bg-card text-foreground hover:bg-muted transition-colors"
    >
      <span className="w-7 h-7 grid place-items-center rounded-full bg-primary/10 dark:bg-accent/15 text-primary dark:text-accent">
        <ArrowLeft className="w-4 h-4" strokeWidth={2} />
      </span>
      Regresar
    </button>
  );

  // ─── Región accesible para anuncios ──────────────────────────────────
  const RegionAviso = () => (
    <p
      data-testid={T.avisoRegion}
      role="status"
      aria-live="polite"
      className={cn("text-sm", aviso ? "text-destructive" : "sr-only")}
    >
      {aviso}
    </p>
  );

  // ─── Bloqueo por límite ──────────────────────────────────────────────
  if (bloqueadoPorLimite) {
    return (
      <div data-testid={T.screen} className="flex flex-col gap-6 -mt-2">
        <BotonRegresar onClick={volverAlEvento} testId={T.btnRegresar} />
        <div
          data-testid={T.limiteBloqueado}
          role="alert"
          className="rounded-2xl border hairline bg-destructive/5 dark:bg-destructive/10 p-6 flex items-start gap-4"
        >
          <span className="w-10 h-10 grid place-items-center rounded-xl bg-destructive/10 text-destructive shrink-0">
            <Ban className="w-5 h-5" strokeWidth={1.7} />
          </span>
          <div>
            <h1 className="font-heading text-xl text-foreground">
              Alcanzaste el límite de boletos
            </h1>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              Ya tienes <strong>{confirmados}</strong> boleto(s) confirmado(s) para este
              evento y el límite es de <strong>{limite}</strong> por socio.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button type="button" data-testid={T.btnVolverEvento} onClick={volverAlEvento}
            className="flex-1 rounded-full px-5 py-3 text-sm font-semibold border hairline bg-card text-foreground hover:bg-muted transition-colors">
            Volver al evento
          </button>
          <button type="button" data-testid={T.btnMisBoletos} onClick={() => navigate("/app/mis-boletos")}
            className="flex-1 rounded-full px-5 py-3 text-sm font-semibold bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft hover:opacity-90 transition-opacity">
            Ir a Mis Boletos
          </button>
        </div>
      </div>
    );
  }

  // ─── Venta pausada ───────────────────────────────────────────────────
  if (ventaPausada) {
    return (
      <div data-testid={T.screen} className="flex flex-col gap-6 -mt-2">
        <BotonRegresar onClick={volverAlEvento} testId={T.btnRegresar} />
        <div data-testid={T.ventaPausada} role="alert"
          className="rounded-2xl border hairline bg-amber-500/5 dark:bg-amber-500/10 p-6 flex items-start gap-4">
          <span className="w-10 h-10 grid place-items-center rounded-xl bg-amber-500/15 text-amber-700 dark:text-amber-300 shrink-0">
            <PauseCircle className="w-5 h-5" strokeWidth={1.7} />
          </span>
          <div>
            <h1 className="font-heading text-xl text-foreground">La venta está pausada</h1>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              Vuelve a intentarlo en unos minutos. No se realizó ningún cargo.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Paso RESUMEN (carrito) ──────────────────────────────────────────
  if (paso === "resumen") {
    const seleccionados = salones
      .filter((s) => Number(cantidades[s.id]) > 0)
      .map((s) => ({
        ...s,
        cantidad: Number(cantidades[s.id]),
        subtotal: Number(cantidades[s.id]) * Number(s.precioPorBoleto || 0),
      }));
    return (
      <div data-testid={T.screen} className="flex flex-col gap-6 pb-28">
        <BotonRegresar onClick={() => setPaso("seleccion")} testId={T.btnRegresar} />
        <header className="animate-fade-up">
          <span className="overline text-accent">Casi listo</span>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-foreground">
            Resumen de compra
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Se realizará un solo pago por todos los boletos seleccionados.
          </p>
        </header>

        <section data-testid={T.resumen} className="rounded-2xl border hairline bg-card overflow-hidden shadow-soft">
          <div className="px-6 py-4 border-b hairline">
            <span className="text-sm text-muted-foreground">Evento</span>
            <p className="text-sm text-foreground font-medium">{evento.nombre}</p>
          </div>
          <ul className="flex flex-col">
            {seleccionados.map((s) => (
              <li key={s.id} data-testid={T.resumenItem(s.id)}
                className="flex items-center justify-between gap-4 px-6 py-4 border-b hairline">
                <div className="min-w-0">
                  <p className="text-sm text-foreground truncate">{s.salon}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {s.cantidad} × {fmt(s.precioPorBoleto)}
                  </p>
                </div>
                <span className="text-sm text-foreground tabular-nums whitespace-nowrap">
                  {fmt(s.subtotal)}
                </span>
              </li>
            ))}
            <li className="flex items-center justify-between gap-4 px-6 py-3 bg-muted/20">
              <span className="text-sm text-muted-foreground">Total de boletos</span>
              <span data-testid={T.resumenTotalBoletos} className="text-sm text-foreground tabular-nums">
                {totalSeleccionado}
              </span>
            </li>
            <li className="flex items-center justify-between gap-4 px-6 py-5 bg-muted/30">
              <span className="overline text-muted-foreground">Total</span>
              <span data-testid={T.resumenTotal} className="font-heading text-2xl text-primary dark:text-accent">
                {fmt(totalGeneral)}
              </span>
            </li>
          </ul>
        </section>

        <p className="text-xs text-muted-foreground">
          La disponibilidad se confirmará al continuar. El importe final lo determina el sistema.
        </p>
        <RegionAviso />

        <div className="flex flex-col gap-3">
          <button
            type="button"
            data-testid={T.btnConfirmar}
            onClick={confirmarCompra}
            disabled={enviando}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-sm font-semibold uppercase tracking-wide bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft hover:opacity-95 active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {enviando ? "Procesando…" : "Confirmar y pagar"}
            <ChevronRight className="w-4 h-4" strokeWidth={2} />
          </button>
          <button type="button" data-testid={T.btnEditar} onClick={() => setPaso("seleccion")}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium border hairline bg-card text-foreground hover:bg-muted transition-colors">
            Editar cantidades
          </button>
        </div>
      </div>
    );
  }

  // ─── Paso SELECCIÓN ──────────────────────────────────────────────────
  return (
    <div data-testid={T.screen} className="flex flex-col gap-6 -mt-2 pb-28">
      <BotonRegresar onClick={volverAlEvento} testId={T.btnRegresar} />

      <header className="animate-fade-up">
        <span className="overline text-accent inline-flex items-center gap-1.5">
          <DoorOpen className="w-3.5 h-3.5" /> Acceso general
        </span>
        <h1 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-foreground">
          Selecciona tus boletos
        </h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-lg leading-relaxed">
          Puedes elegir boletos de uno o varios salones y realizar un solo pago.
        </p>
        {cupoRestante !== null && cupoRestante !== undefined && (
          <p data-testid={T.cupoRestante} className="mt-2 text-xs text-muted-foreground">
            Puedes adquirir hasta <strong>{cupoRestante}</strong> boleto(s) en este evento.
          </p>
        )}
      </header>

      <ul data-testid={T.lista} className="flex flex-col gap-3">
        {salones.map((s) => {
          const cant = Number(cantidades[s.id]) || 0;
          const agotado = Number(s.disponibles) <= 0;
          const topeMas = cant >= s.disponibles || remanenteCupo <= 0;
          const afectado = salonAfectado === s.id;
          const subtotal = cant * Number(s.precioPorBoleto || 0);
          return (
            <li
              key={s.id}
              data-testid={T.salonRow(s.id)}
              className={cn(
                "rounded-2xl border hairline bg-card p-4 flex flex-col gap-3 transition-colors",
                agotado && "opacity-70",
                afectado && "ring-2 ring-destructive/60 border-destructive/50",
              )}
            >
              {afectado && (
                <span
                  data-testid={T.salonAfectado(s.id)}
                  className="sr-only"
                  aria-hidden="true"
                >
                  afectado
                </span>
              )}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-heading text-lg text-foreground truncate">{s.salon}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{fmt(s.precioPorBoleto)} por boleto</p>
                </div>
                {agotado ? (
                  <span data-testid={T.salonAgotado(s.id)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 bg-muted text-muted-foreground ring-1 ring-border">
                    <Ban className="w-3.5 h-3.5" /> Agotado
                  </span>
                ) : (
                  <span data-testid={T.salonDisponibles(s.id)} className="text-xs text-muted-foreground whitespace-nowrap">
                    {s.disponibles} disponibles
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 rounded-full border hairline p-1.5 bg-background">
                  <button
                    type="button"
                    data-testid={T.btnMenos(s.id)}
                    aria-label={`Disminuir boletos de ${s.salon}`}
                    onClick={() => dec(s)}
                    disabled={cant <= 0 || agotado}
                    className="w-11 h-11 grid place-items-center rounded-full bg-muted hover:bg-muted/70 disabled:opacity-40 transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span data-testid={T.cantidad(s.id)} className="w-8 text-center font-heading text-xl text-foreground tabular-nums">
                    {cant}
                  </span>
                  <button
                    type="button"
                    data-testid={T.btnMas(s.id)}
                    aria-label={`Aumentar boletos de ${s.salon}`}
                    onClick={() => inc(s)}
                    disabled={topeMas || agotado}
                    className="w-11 h-11 grid place-items-center rounded-full bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground disabled:opacity-40 transition-opacity"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-right">
                  <span className="overline text-muted-foreground">Subtotal</span>
                  <p data-testid={T.salonSubtotal(s.id)} className="text-sm text-foreground tabular-nums">
                    {fmt(subtotal)}
                  </p>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <RegionAviso />

      {/* Barra de totales */}
      <section className="rounded-2xl border hairline bg-card p-5 shadow-soft flex flex-col gap-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Boletos seleccionados</span>
          <span data-testid={T.totalBoletos} className="text-foreground tabular-nums">{totalSeleccionado}</span>
        </div>
        {cupoDespues !== null && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Cupo restante después de esta compra</span>
            <span className="tabular-nums">{cupoDespues}</span>
          </div>
        )}
        <div className="flex items-center justify-between border-t hairline pt-2 mt-1">
          <span className="overline text-muted-foreground">Total</span>
          <span data-testid={T.totalGeneral} className="font-heading text-xl text-primary dark:text-accent">
            {fmt(totalGeneral)}
          </span>
        </div>
      </section>

      <button
        type="button"
        data-testid={T.btnContinuar}
        onClick={irAResumen}
        disabled={totalSeleccionado <= 0}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-sm font-semibold uppercase tracking-wide bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft hover:opacity-95 active:scale-[0.99] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Continuar
        <ChevronRight className="w-4 h-4" strokeWidth={2} />
      </button>
    </div>
  );
}
